import React, { useEffect, useState } from "react";
import { Auth, validatePassword, PasswordValidationStatus } from "firebase/auth";
import { IoCheckmarkCircle, IoEllipseOutline } from "react-icons/io5";
import { auth } from "../../config/firebase";

const CheckIcon = IoCheckmarkCircle as any;
const EmptyIcon = IoEllipseOutline as any;

// The SDK caches the policy only after its first fetch resolves, so concurrent
// validatePassword calls (one per keystroke) would each fetch it and quickly hit
// Firebase's quota. Load it once per Auth project, then validate against the cached copy.
const policyLoads = new Map<string, Promise<unknown>>();
const loadPolicy = (authInstance: Auth) => {
  const key = authInstance.app.name;
  let load = policyLoads.get(key);
  if (!load) {
    load = validatePassword(authInstance, "").catch((err) => {
      policyLoads.delete(key);
      throw err;
    });
    policyLoads.set(key, load);
  }
  return load;
};

/**
 * Validates `password` against the live Firebase Auth password policy (set in the
 * Firebase console), so the rules shown always match what Firebase enforces.
 * `status` is null until the policy has loaded. If it can't be loaded,
 * `unavailable` is true: callers should let the user submit and rely on
 * Firebase rejecting a non-compliant password server-side.
 */
export const usePasswordValidation = (password: string, authInstance: Auth = auth) => {
  const [status, setStatus] = useState<PasswordValidationStatus | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadPolicy(authInstance)
      .then(() => validatePassword(authInstance, password))
      .then((s) => {
        if (cancelled) return;
        setStatus(s);
        setUnavailable(false);
      })
      .catch((err) => {
        console.error("Password policy check failed:", err);
        if (!cancelled) setUnavailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, [password, authInstance]);

  return { status, unavailable };
};

const Rule: React.FC<{ met: boolean; label: string }> = ({ met, label }) => (
  <li className={`flex items-center gap-2 ${met ? "text-brand-700" : "text-ink-muted"}`}>
    {met ? (
      <CheckIcon className="h-4 w-4 flex-shrink-0 text-brand-500" />
    ) : (
      <EmptyIcon className="h-4 w-4 flex-shrink-0 text-ink-faint" />
    )}
    <span>{label}</span>
  </li>
);

/** Checklist of the password rules; a rule is only listed if the policy requires it. */
const PasswordRequirements: React.FC<{ status: PasswordValidationStatus | null }> = ({ status }) => {
  if (!status) return null;
  const opts = status.passwordPolicy.customStrengthOptions;

  const rules: { met?: boolean; label: string }[] = [
    { met: status.meetsMinPasswordLength, label: `At least ${opts.minPasswordLength ?? 6} characters` },
    { met: status.containsUppercaseLetter, label: "An uppercase letter" },
    { met: status.containsLowercaseLetter, label: "A lowercase letter" },
    { met: status.containsNumericCharacter, label: "A number" },
    { met: status.containsNonAlphanumericCharacter, label: "A special character (e.g. ! ? # @)" },
  ];
  // The max length (4096) is only worth mentioning once someone exceeds it.
  if (status.meetsMaxPasswordLength === false) {
    rules.push({ met: false, label: `At most ${opts.maxPasswordLength} characters` });
  }

  return (
    <ul className="space-y-1 text-sm" aria-label="Password requirements">
      {rules
        .filter((r) => r.met !== undefined)
        .map((r) => (
          <Rule key={r.label} met={!!r.met} label={r.label} />
        ))}
    </ul>
  );
};

export default PasswordRequirements;
